import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LeagueMember } from '../pages/liga/utils/leagueMembersUtils';
import Load from './Load';

interface LeagueMembersListProps {
  members: LeagueMember[];
  loading: boolean;
  error: string | null;
  emptyMessage?: string;
  alunosIds?: Set<string>;
  selectedIds?: Set<string>;
  savingAlunoId?: string | null;
  onToggleSelect?: (atletaId: string) => void;
  onToggleAluno?: (atletaId: string, isAluno: boolean) => void;
  onMarcarSelecionados?: () => void;
  onDesmarcarSelecionados?: () => void;
}

const LeagueMembersList: React.FC<LeagueMembersListProps> = ({
  members,
  loading,
  error,
  emptyMessage = 'Nenhum membro encontrado.',
  alunosIds = new Set(),
  selectedIds = new Set(),
  savingAlunoId = null,
  onToggleSelect,
  onToggleAluno,
  onMarcarSelecionados,
  onDesmarcarSelecionados,
}) => {
  const navigate = useNavigate();

  if (loading) {
    return <Load />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
        <div className="card-body text-center py-5">
          <p className="text-grey-500 mb-0">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const handleRowClick = (m: LeagueMember) => {
    if (m.atletaId) {
      navigate(`/userpage/${m.atletaId}`);
    }
  };

  const rowGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '32px 80px 48px minmax(150px, 1.5fr) minmax(180px, 1.5fr) minmax(200px, 2fr) 90px',
    alignItems: 'center',
    gap: '0.75rem 1rem',
    minWidth: 'max-content',
    direction: 'ltr',
  };

  const membrosComAtleta = members.filter((m) => m.atletaId);
  const todosSelecionados = membrosComAtleta.length > 0 && membrosComAtleta.every((m) => selectedIds.has(m.atletaId!));
  const algunsSelecionados = membrosComAtleta.some((m) => selectedIds.has(m.atletaId!));

  return (
    <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
      <div className="card-body p-0">
        {/* Wrapper com scroll horizontal para telas menores */}
        <div style={{ overflowX: 'auto', width: '100%' }}>
          {/* Barra de ações em massa */}
          {selectedIds.size > 0 && (
            <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
              <span className="font-xsss text-grey-700">{selectedIds.size} selecionado(s)</span>
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={onMarcarSelecionados}
              >
                Marcar como aluno
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={onDesmarcarSelecionados}
              >
                Desmarcar aluno
              </button>
            </div>
          )}
          <div className="list-group list-group-flush">
            {/* Cabeçalho com checkbox selecionar todos */}
            {membrosComAtleta.length > 0 && onToggleSelect && (
              <div className="px-3 py-2 border-bottom d-flex align-items-center gap-2 bg-grey-50">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={todosSelecionados}
                  ref={(el) => { if (el) el.indeterminate = algunsSelecionados && !todosSelecionados; }}
                  onChange={() => {
                    if (todosSelecionados) {
                      membrosComAtleta.forEach((m) => selectedIds.has(m.atletaId!) && onToggleSelect(m.atletaId!));
                    } else {
                      membrosComAtleta.forEach((m) => !selectedIds.has(m.atletaId!) && onToggleSelect(m.atletaId!));
                    }
                  }}
                  aria-label="Selecionar todos"
                />
                <span className="font-xsss text-grey-500">Selecionar todos</span>
              </div>
            )}
            {members.map((membro) => {
              const isClickable = Boolean(membro.atletaId);
              const isAluno = membro.atletaId ? alunosIds.has(membro.atletaId) : false;
              const isSelected = membro.atletaId ? selectedIds.has(membro.atletaId) : false;
              const isSaving = membro.atletaId === savingAlunoId;
              const col5Text =
                membro.isColaborador && membro.organizadorContextos.length > 0
                  ? membro.organizadorContextos.map((c) => `${c.nome} (${c.cargo})`).join(' · ')
                  : membro.torneiosInscritos.length > 0
                    ? membro.torneiosInscritos.map((t) => t.nome).join(' · ')
                    : null;
              return (
                <div
                  key={membro.key}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={() => isClickable && handleRowClick(membro)}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleRowClick(membro);
                    }
                  }}
                  className={`list-group-item py-3 px-3 ${
                    isClickable ? 'list-group-item-action cursor-pointer' : ''
                  } ${isSelected ? 'table-active' : ''}`}
                  style={{
                    cursor: isClickable ? 'pointer' : 'default',
                    ...rowGridStyle,
                  }}
                >
                {/* Col 0: Checkbox de seleção */}
                <div style={{ gridColumn: 1 }} onClick={(e) => e.stopPropagation()}>
                  {membro.atletaId && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isSelected}
                      onChange={() => onToggleSelect?.(membro.atletaId!)}
                      aria-label={`Selecionar ${membro.nome}`}
                    />
                  )}
                </div>

                {/* Col 1: Ícones de papel */}
                <div
                  className="d-flex gap-1"
                  style={{ minWidth: 0, gridColumn: 2 }}
                >
                  {membro.isColaborador && (
                    <i
                      className="bi bi-person-gear btn-round-sm bg-primary text-white shadow-xss d-inline-flex align-items-center justify-content-center"
                      title="Colaborador"
                      aria-label="Colaborador"
                      style={{ width: 28, height: 28, fontSize: '14px' }}
                    />
                  )}
                  {membro.isSeguidor && (
                    <i
                      className="bi bi-person-check btn-round-sm bg-info text-white shadow-xss d-inline-flex align-items-center justify-content-center"
                      title="Seguidor"
                      aria-label="Seguidor"
                      style={{ width: 28, height: 28, fontSize: '14px' }}
                    />
                  )}
                  {membro.isJogador && (
                    <i
                      className="bi bi-person-video2 btn-round-sm bg-success text-white shadow-xss d-inline-flex align-items-center justify-content-center"
                      title="Jogador"
                      aria-label="Jogador"
                      style={{ width: 28, height: 28, fontSize: '14px' }}
                    />
                  )}
                </div>

                {/* Col 2: Avatar */}
                <figure
                  className="avatar mb-0"
                  style={{ width: 40, height: 40, minWidth: 40, gridColumn: 3 }}
                >
                  <img
                    src={membro.imagemUrl || '/assets/images/user.png'}
                    alt={membro.nome}
                    className="rounded-circle w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                </figure>

                {/* Col 3: Nome e username */}
                <div className="min-w-0" style={{ gridColumn: 4 }}>
                  <div className="fw-600 text-grey-900 text-truncate">
                    {membro.nome || '—'}
                  </div>
                  {membro.username ? (
                    <div className="font-xssss text-primary text-truncate">
                      @{membro.username}
                    </div>
                  ) : (
                    <div className="font-xssss text-grey-400">&nbsp;</div>
                  )}
                </div>

                {/* Col 4: Email/Telefone (exibido se jogador inscrito) */}
                <div
                  className="text-grey-500 font-xsss"
                  style={{ gridColumn: 5, minWidth: 0 }}
                >
                  {membro.isJogador && (membro.email || membro.telefone) ? (
                    <>
                      {membro.email && (
                        <div className="text-truncate" title={membro.email}>
                          <i className="bi bi-envelope me-1" />{membro.email}
                        </div>
                      )}
                      {membro.telefone && (
                        <div className="text-truncate" title={membro.telefone}>
                          <i className="bi bi-telephone me-1" />{membro.telefone}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-grey-400">&mdash;</span>
                  )}
                </div>

                {/* Col 5: Torneios vinculados */}
                <div
                  className="text-grey-700 font-xsss text-truncate min-w-0"
                  style={{ gridColumn: 6 }}
                  title={col5Text ?? undefined}
                >
                  {col5Text ?? '\u00A0'}
                </div>

                {/* Col 6: Toggle aluno */}
                <div
                  style={{ gridColumn: 7 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {membro.atletaId && (
                    <div className="d-flex flex-column align-items-center gap-1">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`aluno-${membro.atletaId}`}
                          checked={isAluno}
                          disabled={isSaving}
                          onChange={() => onToggleAluno?.(membro.atletaId!, isAluno)}
                          aria-label={`Marcar ${membro.nome} como aluno`}
                        />
                      </div>
                      {isAluno && (
                        <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }}>Aluno</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueMembersList;
